// DashHeader — concise, action-led headline. The eyebrow line collapses
// when `summary.eyebrow` is null. The H1 reads from `decisionCount` when
// any decisions are pending, falling back to `openCount` otherwise (so we
// don't display "0 things need your call" on a clean day).

import type { JSX } from "react";

import type { DashboardSummary } from "@loom/shared/dashboard";

import { useT, type Translator } from "../../i18n/index.js";

import { Icon } from "./Icon";

interface DashHeaderProps {
  readonly summary: DashboardSummary | null;
  readonly onNewRequest: () => void;
  readonly onAsk: () => void;
}

const headline = (summary: DashboardSummary, t: Translator): string => {
  if (summary.decisionCount > 0) {
    const n = summary.decisionCount;
    return n === 1
      ? t("dashboard.header.decisionsOne")
      : t("dashboard.header.decisionsMany", { n });
  }
  if (summary.openCount > 0) {
    const n = summary.openCount;
    return n === 1
      ? t("dashboard.header.openOne")
      : t("dashboard.header.openMany", { n });
  }
  return t("dashboard.header.nothingPending");
};

const subline = (summary: DashboardSummary, t: Translator): string => {
  if (summary.decisionCount > 0) {
    return t("dashboard.header.subDecisions");
  }
  return t("dashboard.header.subDefault");
};

export const DashHeader = ({
  summary,
  onNewRequest,
  onAsk,
}: DashHeaderProps): JSX.Element => {
  const t = useT();
  return (
  <header className="dash-header">
    <div className="dash-header-text">
      {summary?.eyebrow && (
        <div className="dash-eyebrow">
          <span className="dot" />
          <span>{summary.eyebrow}</span>
        </div>
      )}
      {summary ? (
        <>
          <h1 className="dash-h1">{headline(summary, t)}</h1>
          <p className="dash-sub">{subline(summary, t)}</p>
        </>
      ) : (
        <>
          <h1 className="dash-h1" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="skeleton" style={{ display: "inline-block", width: 320, height: 28 }} />
          </h1>
          <p className="dash-sub">
            <span className="skeleton" style={{ display: "inline-block", width: 420, height: 14 }} />
          </p>
        </>
      )}
    </div>
    <div className="dash-header-actions">
      <button type="button" className="btn-ghost-lg" onClick={onAsk}>
        <Icon name="chat" size={14} /> {t("dashboard.header.askLoom")}
      </button>
      <button type="button" className="start-btn" onClick={onNewRequest}>
        <span className="start-glyph" aria-hidden="true">
          <Icon name="plus" size={14} />
        </span>
        <span>{t("dashboard.header.newRequest")}</span>
        <span className="start-kbd">⌘N</span>
      </button>
    </div>
    </header>
  );
};
